import axios from 'axios';
import React from 'react';
import { getDriverApiBaseUrl } from '../../utils/utils';

// ─────────────────────────────────────────────────────────────────────────────
// Forwarded Orders — API layer.  READ-ONLY MODULE.
//
// There is NOTHING here that writes. Retry, approve, cancel and manual-handover
// were removed on purpose: a forward creates a REAL, BILLABLE shipment at the
// carrier, and neither half of this app has working route-level authorisation to
// stand in front of a button that spends money. Reads only. If a write is ever
// wanted back, it needs an authorisation story first, not a new function here.
//
// The browser never talks to the main (CRM) backend for this module. Everything
// goes through the driver backend at /shypvdriverapp/api/forwarded-orders, which
// in turn reads the main backend's internal, IP-restricted forwarding surface.
// Label bytes in particular are streamed by the driver backend, because the
// carrier's own label URL carries our account API key in its query string and
// must never reach a browser.
//
// Deliberately NOT used here: getCrmApiBaseUrl(), and REACT_APP_API_URL raw.
// ─────────────────────────────────────────────────────────────────────────────

// getDriverApiBaseUrl() strips a trailing /shypvdriverapp, so add it back.
const API_BASE = `${getDriverApiBaseUrl()}/shypvdriverapp/api/forwarded-orders`;

// WHEREVER THE APP ACTUALLY PUT THE TOKEN.
//
// This used to read sessionStorage['usertoken'] and nothing else, and that is one of four places
// this app stores a session in: it writes `usertoken` to BOTH sessionStorage and localStorage
// (the persistent "stay signed in" path restores from localStorage), and other screens read a
// `token` key as well. A signed-in user whose session came back from localStorage — a reopened
// tab, a restored window — therefore sent NO Authorization header at all, and the console answered
// "Invalid access token" to somebody who was perfectly well signed in.
//
// So: try every place, in most-specific-first order, and use the first one that holds anything.
// Reading extra keys costs nothing and removes a whole class of "it works for me" difference
// between tabs. Wrapped in try/catch because storage access itself throws in some privacy modes.
const TOKEN_KEYS = ['usertoken', 'token'];

const readToken = () => {
    for (const store of [sessionStorage, localStorage]) {
        for (const key of TOKEN_KEYS) {
            try {
                const v = store.getItem(key);
                if (v && String(v).trim()) return String(v).trim();
            } catch (e) {
                /* storage blocked — try the next one */
            }
        }
    }
    return null;
};

const authHeaders = () => {
    const t = readToken();
    return t ? { Authorization: `Bearer ${t}` } : {};
};

// House envelope: { success, data, info, errors:[{errorMessage,errorDetails}] }
const unwrap = (res, fb) => (res && res.data && res.data.data != null ? res.data.data : fb);

const errMsg = (e, fb) => {
    const errs = e && e.response && e.response.data && e.response.data.errors;
    if (Array.isArray(errs) && errs.length) {
        const joined = errs
            .map((x) => (x && (x.errorDetails || x.errorMessage)) || (typeof x === 'string' ? x : ''))
            .filter(Boolean)
            .join(', ');
        if (joined) return joined;
    }
    return (e && e.response && e.response.data && e.response.data.message) || (e && e.message) || fb;
};

// Every failure becomes an Error carrying .status, so a caller can tell a 401
// ("sign in again") and a 503 ("this server has no forwarding link configured")
// apart from a plain network fault, and say which one happened.
const normalise = (e, fb) => {
    const err = new Error(errMsg(e, fb));
    err.status = (e && e.response && e.response.status) || 0;
    err.cause = e;
    return err;
};

const get = async (path, config, fb) => {
    try {
        const res = await axios.get(`${API_BASE}${path}`, { ...(config || {}), headers: authHeaders() });
        return unwrap(res, fb);
    } catch (e) {
        throw normalise(e, 'Request failed');
    }
};

// ── Reads — the entire surface of this module ───────────────────────────────

export const listShipments = (params) =>
    get('', { params: params || {} }, { items: [], total: 0, page: 0, size: 25, viewer: null });

export const getShipment = (id) => get(`/${id}`, null, null);

export const getCarrierEvents = (id) => get(`/${id}/carrier-events`, null, []);

export const getPartnerEvents = (id) => get(`/${id}/partner-events`, null, null);

// Metadata only — never the label bytes and never a carrier URL.
//
// NOTE FOR WHOEVER CALLS THIS: when nothing is stored locally the main backend
// answers it by making a live, READ-ONLY label GET to the carrier for an AWB
// that already exists. It books nothing, but it is still an outbound carrier
// call, so the detail page asks for it only when the Label tab is opened rather
// than on every page view.
export const getLabelMeta = (id) => get(`/${id}/label`, null, null);

export const listCarriers = () => get('/carriers', null, []);

export const getSummary = (carrierCode) => get('/summary', { params: { carrierCode } }, null);

// Label bytes, streamed by the driver backend. Returns a Blob.
// The 404 body for "no label available" is plain text, not the JSON envelope,
// so the blob has to be read back as text to surface the reason.
export const getLabelBlob = async (id) => {
    try {
        const res = await axios.get(`${API_BASE}/${id}/label-file`, {
            responseType: 'blob',
            headers: authHeaders(),
        });
        return res.data;
    } catch (e) {
        const body = e && e.response && e.response.data;
        if (body && typeof body.text === 'function') {
            let text = '';
            try { text = await body.text(); } catch (_) { text = ''; }
            let msg = text;
            try {
                const parsed = JSON.parse(text);
                const errs = parsed && parsed.errors;
                if (Array.isArray(errs) && errs.length) {
                    msg = errs.map((x) => (x && (x.errorDetails || x.errorMessage)) || '').filter(Boolean).join(', ');
                }
            } catch (_) { /* plain text body — use as-is */ }
            const err = new Error(msg || 'Could not load the label file');
            err.status = (e.response && e.response.status) || 0;
            throw err;
        }
        throw normalise(e, 'Could not load the label file');
    }
};

// ── Vocabulary ──────────────────────────────────────────────────────────────
// These strings are the SERVER's, copied from the Java that emits them. Nothing
// here invents a synonym: a screen whose vocabulary does not match the backend's
// silently turns a disagreement into apparent agreement, which is the one bug
// this screen exists to prevent.

// ForwardedShipmentDto.status — ForwardingStatus in the main backend.
export const FORWARDING_STATUSES = [
    'PENDING', 'AWAITING_APPROVAL', 'AWAITING_CONFIG', 'SUSPENDED', 'INFLIGHT',
    'INFLIGHT_UNKNOWN', 'RETRY_SCHEDULED', 'FORWARDED', 'PENDING_MANUAL',
    'MANUAL_FORWARDED', 'IN_TRANSIT', 'EXCEPTION', 'DELIVERED', 'RETURNED',
    'CANCEL_REQUESTED', 'CANCEL_MANUAL_REQUIRED', 'CANCELLED', 'AMEND_REQUESTED',
    'AMEND_MANUAL_REQUIRED', 'FAILED_PERMANENT', 'SUPERSEDED',
];

// ForwardingStatusTrioDto.agreement — EXACTLY these three values, and no others.
// ForwardingAdminService.compareSides() sets "AGREE" when every comparable side
// matched, "DIVERGENT" when they did not, and "INSUFFICIENT_DATA" when fewer
// than two sides had a comparable status.
export const AGREEMENT = {
    AGREE: 'AGREE',
    DIVERGENT: 'DIVERGENT',
    INSUFFICIENT_DATA: 'INSUFFICIENT_DATA',
};

// ForwardingStatusTrioDto.comparedSides / .stalestSide.
export const SIDE_KEYS = { OURS: 'OURS', PARTNER: 'PARTNER', CARRIER: 'CARRIER' };

/**
 * How to paint one agreement value.
 *
 * A value the server did not promise is NOT coerced into one of the three. It is
 * shown as unrecognised, with the literal string, because rendering an unknown
 * word as "AGREE" is exactly the failure this screen must not have.
 */
export const agreementView = (raw) => {
    const value = raw === null || raw === undefined ? '' : String(raw).trim().toUpperCase();
    if (value === AGREEMENT.AGREE) {
        return {
            value,
            known: true,
            loud: false,
            bg: '#dcfce7', fg: '#14532d', border: '#16a34a', icon: 'fa-check-circle',
            head: 'The sides that could be compared AGREE',
        };
    }
    if (value === AGREEMENT.DIVERGENT) {
        return {
            value,
            known: true,
            loud: true,
            bg: '#fee2e2', fg: '#7f1d1d', border: '#dc2626', icon: 'fa-exclamation-triangle',
            head: 'DIVERGENT — these systems disagree about this leg',
        };
    }
    if (value === AGREEMENT.INSUFFICIENT_DATA) {
        return {
            value,
            known: true,
            loud: false,
            bg: '#f1f5f9', fg: '#334155', border: '#94a3b8', icon: 'fa-question-circle',
            head: 'INSUFFICIENT DATA — fewer than two sides could be compared',
        };
    }
    return {
        value: value || '(empty)',
        known: false,
        loud: true,
        bg: '#fef3c7', fg: '#78350f', border: '#d97706', icon: 'fa-question-circle',
        head: 'Unrecognised agreement value from the server',
    };
};

// ── Formatting — every one of these is null-safe on purpose ─────────────────
// There is no ErrorBoundary in this app: a single unguarded .toFixed() on a
// null takes the whole shell down. Nothing below ever throws.

export const txt = (v, fb = '—') => {
    if (v === null || v === undefined) return fb;
    const s = String(v);
    return s.trim() === '' ? fb : s;
};

export const num = (v, digits = 2) => {
    const n = Number(v);
    if (v === null || v === undefined || v === '' || isNaN(n)) return '—';
    return n.toFixed(digits);
};

export const money = (v, currency) => {
    const n = Number(v);
    if (v === null || v === undefined || v === '' || isNaN(n)) return '—';
    const f = n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return currency ? `${f} ${currency}` : f;
};

export const dt = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
};

export const ago = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    const sec = Math.round((Date.now() - d.getTime()) / 1000);
    if (sec < 0) return 'in the future';
    if (sec < 45) return 'just now';
    const min = Math.round(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.round(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.round(hr / 24);
    return `${day}d ago`;
};

// ── Shared styling (kept here so both pages stay identical) ─────────────────

export const INPUT = {
    width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1',
    borderRadius: 6, fontSize: 13, boxSizing: 'border-box', background: '#fff',
};
export const TH = {
    textAlign: 'left', padding: '8px 10px', background: '#f8fafc', color: '#475569',
    fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em',
    borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap',
};
export const TD = {
    padding: '9px 10px', borderBottom: '1px solid #f1f5f9', color: '#0f172a',
    verticalAlign: 'top', fontSize: 12,
};
const BTN_BASE = {
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px',
    borderRadius: 7, fontSize: 13, fontWeight: 500, cursor: 'pointer', border: 'none',
};
// Only three: this module has no destructive control left to paint red.
export const BTN = {
    primary: { ...BTN_BASE, background: '#2563eb', color: '#fff' },
    ghost: { ...BTN_BASE, background: '#fff', color: '#475569', border: '1px solid #cbd5e1' },
    disabled: { ...BTN_BASE, background: '#e2e8f0', color: '#94a3b8', cursor: 'not-allowed' },
};

// Tone for a SHYPV-VOCABULARY status pill. Anything unrecognised falls back to
// neutral rather than blowing up or pretending it is fine. Do not pass a
// carrier's own raw word through here — that is a different vocabulary, and a
// neutral pill is the honest rendering for it (see RawPill).
export const statusTone = (status) => {
    const s = (status || '').toUpperCase();
    if (['DELIVERED', 'FORWARDED', 'MANUAL_FORWARDED'].includes(s)) return { bg: '#dcfce7', fg: '#15803d' };
    if (['IN_TRANSIT', 'INFLIGHT', 'PENDING', 'AMEND_REQUESTED'].includes(s)) return { bg: '#e0f2fe', fg: '#0369a1' };
    if (['AWAITING_APPROVAL', 'AWAITING_CONFIG', 'PENDING_MANUAL', 'RETRY_SCHEDULED',
        'SUSPENDED', 'INFLIGHT_UNKNOWN', 'CANCEL_REQUESTED', 'CANCEL_MANUAL_REQUIRED',
        'AMEND_MANUAL_REQUIRED'].includes(s)) return { bg: '#fef3c7', fg: '#b45309' };
    if (['FAILED_PERMANENT', 'EXCEPTION', 'RETURNED'].includes(s)) return { bg: '#fee2e2', fg: '#991b1b' };
    if (['CANCELLED', 'SUPERSEDED'].includes(s)) return { bg: '#e2e8f0', fg: '#475569' };
    return { bg: '#f1f5f9', fg: '#475569' };
};

// ── Small shared presentational components ──────────────────────────────────

const PILL_BASE = {
    display: 'inline-block', padding: '3px 9px', borderRadius: 11, fontSize: 11,
    fontWeight: 600, whiteSpace: 'nowrap',
};

/** A status in the SHYPV vocabulary. */
export function StatusPill({ status, title }) {
    const tone = statusTone(status);
    return (
        <span title={title || status || 'unknown'} style={{ ...PILL_BASE, background: tone.bg, color: tone.fg }}>
            {txt(status, 'UNKNOWN')}
        </span>
    );
}

/** A word in SOMEONE ELSE'S vocabulary (a carrier's own status text). Never toned. */
export function RawPill({ value, title }) {
    return (
        <span
            title={title || 'the carrier’s own wording'}
            style={{ ...PILL_BASE, background: '#0f172a', color: '#e2e8f0', fontFamily: 'monospace' }}
        >
            {txt(value, 'UNKNOWN')}
        </span>
    );
}

/** "unknown" as a first-class value — never substituted with another side's word. */
export function UnknownPill({ label }) {
    return (
        <span style={{ ...PILL_BASE, background: '#e2e8f0', color: '#475569', fontStyle: 'italic' }}>
            {label || 'unknown'}
        </span>
    );
}

export function Card({ title, subtitle, right, children, style }) {
    return (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 18, marginBottom: 14, ...(style || {}) }}>
            {(title || right) && (
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
                    <div>
                        {title && <div style={{ fontWeight: 600, color: '#0f172a', fontSize: 14 }}>{title}</div>}
                        {subtitle && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{subtitle}</div>}
                    </div>
                    {right}
                </div>
            )}
            {children}
        </div>
    );
}

export function Field({ label, children }) {
    return (
        <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                {label}
            </label>
            {children}
        </div>
    );
}

export function Alert({ children, tone = 'error' }) {
    const tones = {
        error: { bg: '#fee2e2', fg: '#991b1b', icon: 'fa-exclamation-triangle' },
        warn: { bg: '#fef3c7', fg: '#92400e', icon: 'fa-exclamation-circle' },
        info: { bg: '#e0f2fe', fg: '#075985', icon: 'fa-info-circle' },
        ok: { bg: '#dcfce7', fg: '#166534', icon: 'fa-check-circle' },
    };
    const t = tones[tone] || tones.error;
    return (
        <div style={{ marginTop: 10, padding: '10px 14px', borderRadius: 8, background: t.bg, color: t.fg, fontSize: 13, lineHeight: 1.5 }}>
            <i className={`fa ${t.icon}`} style={{ marginRight: 6 }} />
            {children}
        </div>
    );
}

/** A read-only badge. This module has no operator tier any more — everyone reads. */
export function ReadOnlyBadge({ email }) {
    return (
        <span style={{ fontSize: 11, color: '#64748b' }}>
            {txt(email, 'signed in')}
            <span style={{ ...PILL_BASE, marginLeft: 6, fontSize: 10, background: '#f1f5f9', color: '#475569' }}>
                READ ONLY
            </span>
        </span>
    );
}

// Every table in this module gets one of these instead of rendering nothing.
export function EmptyRow({ colSpan, children }) {
    return (
        <tr>
            <td colSpan={colSpan} style={{ padding: 26, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                {children}
            </td>
        </tr>
    );
}

export function Spinner({ label }) {
    return (
        <div style={{ padding: 26, textAlign: 'center', color: '#64748b', fontSize: 13 }}>
            <i className="fa fa-spinner fa-spin" style={{ marginRight: 8 }} />
            {label || 'Loading…'}
        </div>
    );
}
